import { useState, useEffect, useMemo } from "react";
import { Input } from "@rumbledirect/ui";
import { Form, Badge, FormProps, Switch } from "antd";
import { SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { DossierFilterValues } from "../lib/types/DossierFilterValues.interface";
import { dossierFilterFields } from "../lib/constants/dossierFilterFields";
import { buildCompleteFilterForm } from "@/lib/helpers/buildCompleteFilterForm";
import { useQuery } from "@tanstack/react-query";
import {
  fetchServicepartnersWithoutPagination,
  type ServicePartnerBrief,
} from "@/lib/api/resources/servicePartners";
import { Modal } from "@/components/v2";
import { Button, Card, CardFooter, Select } from "@rumbledirect/ui";

interface DossiersFilterModalProps {
  setDossierFilters: (filterValues: DossierFilterValues) => void;
  dossierFilters?: DossierFilterValues;
}

const validFilterNames = new Set<string>(
  dossierFilterFields.map((f) => f.name),
);

const hasActiveFilterValue = (value: unknown) =>
  value != null && value !== "" && value !== false && value !== "false";

const normalizeBooleanFilterValue = (value: unknown) =>
  value === true || value === "true" ? true : undefined;

const normalizeDossierFiltersForForm = (
  values: DossierFilterValues,
): DossierFilterValues => ({
  ...values,
  archived: normalizeBooleanFilterValue(values.archived),
  preferredServicePartner: normalizeBooleanFilterValue(
    values.preferredServicePartner,
  ),
});

export const DossiersFilterModal = ({
  setDossierFilters,
  dossierFilters = {},
}: DossiersFilterModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const { data: servicePartners, isLoading: isServicePartnersLoading } =
    useQuery({
      queryKey: ["servicePartners", "brief"],
      queryFn: () => fetchServicepartnersWithoutPagination(),
    });

  const servicePartnerOptions = Array.isArray(servicePartners)
    ? servicePartners.map((servicePartner: ServicePartnerBrief) => ({
        label: servicePartner.fullName,
        value: servicePartner.id,
      }))
    : [];

  const activeFiltersCount = useMemo(
    () =>
      Object.entries(dossierFilters).filter(
        ([k, v]) => validFilterNames.has(k) && hasActiveFilterValue(v),
      ).length,
    [dossierFilters],
  );

  useEffect(() => {
    // Write every known field so cleared filters are reset rather than merged.
    form.setFieldsValue(
      buildCompleteFilterForm(
        dossierFilterFields,
        normalizeDossierFiltersForForm(dossierFilters),
      ),
    );
  }, [dossierFilters, form]);

  const handleSubmit: FormProps<DossierFilterValues>["onFinish"] = (values) => {
    // Filter out empty values
    const filteredValues: DossierFilterValues = Object.fromEntries(
      Object.entries(values).filter(([, value]) => hasActiveFilterValue(value)),
    );

    filteredValues.preferredServicePartner = normalizeBooleanFilterValue(
      filteredValues.preferredServicePartner,
    );
    filteredValues.archived = normalizeBooleanFilterValue(
      filteredValues.archived,
    );

    setDossierFilters(filteredValues);
    setIsModalOpen(false);
  };

  const handleReset = () => {
    form.resetFields();
    setDossierFilters({});
    setIsModalOpen(false);
  };

  return (
    <>
      <Badge count={activeFiltersCount}>
        <Button
          icon={<FilterOutlined />}
          onClick={() => setIsModalOpen(true)}
          variant="light"
        >
          Filters
        </Button>
      </Badge>

      <Modal open={isModalOpen} onCancel={() => setIsModalOpen(false)}>
        <Card title="Dossier filters">
          <p style={{ marginBottom: 24, color: "#666" }}>
            Filter de dossiers op basis van de onderstaande criteria.
          </p>

          <Form
            form={form}
            layout="vertical"
            initialValues={normalizeDossierFiltersForForm(dossierFilters)}
            onFinish={handleSubmit}
          >
            {dossierFilterFields.map((field) => {
              const isServicePartnerField = field.name === "servicePartnerId";

              return (
                <Form.Item
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  rules={field.rules}
                  valuePropName={field.type === "boolean" ? "checked" : "value"}
                >
                  {field.type === "select" ? (
                    <Select
                      placeholder={field.placeholder}
                      allowClear
                      // Provide dynamic options for service partner field
                      options={
                        isServicePartnerField
                          ? servicePartnerOptions
                          : field.options
                      }
                      showSearch
                      optionFilterProp="label"
                      loading={Boolean(
                        isServicePartnerField && isServicePartnersLoading,
                      )}
                    />
                  ) : field.type === "boolean" ? (
                    <Switch />
                  ) : (
                    <Input placeholder={field.placeholder} allowClear />
                  )}
                </Form.Item>
              );
            })}

            <CardFooter>
              <Button onClick={() => setIsModalOpen(false)}>Annuleren</Button>

              <Button onClick={handleReset} className="ml-auto">
                Filters wissen
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SearchOutlined />}
              >
                Filter toepassen
              </Button>
            </CardFooter>
          </Form>
        </Card>
      </Modal>
    </>
  );
};
